import { useRBACPermissions } from '@/contexts/useRBACPermissions'

export type ResolveIngestPermissions = {
  canResolve: boolean
  /**
   * Whether the roles query has settled. Until it does `canResolve` reads
   * `false`, so a caller that only hides UI self-corrects when the roles
   * arrive, but one that acts irreversibly on a denial, such as redirecting,
   * must wait for this or it bounces deep links before the roles are fetched.
   * A failed query counts as settled: deny rather than hang on a skeleton.
   */
  isResolved: boolean
}

/**
 * Mirrors the backend guard on `POST /api/v3/salesforce-opportunities/resolve`,
 * which is `{ADMIN, GTM}` — the same pair as every licence operation, because
 * resolving an ingest creates licences.
 *
 * Deliberately a second hook rather than a widening of
 * `useCanManageSalesforceLink`. That guard is `{ADMIN, OPERATOR, READER}` and
 * its docblock is explicit that the fix for a person who cannot reach a
 * surface is their roles, never the guard. The two screens share a route
 * parent and nothing else.
 *
 * A `gtm`-only holder will not see the sidebar entry that leads here, since
 * it is gated on the link feature's wider read role. That is not this
 * feature's bug: per `docs/permissions-audit.md` F1 such a holder cannot even
 * fetch `getUserRoles` (`{ADMIN, READER}`) and has a degraded UI throughout,
 * which is why every `gtm` grant is hand-paired with `reader` in migrations.
 * The root cause is APP-2472.
 */
export function useCanResolveSalesforceIngest(): ResolveIngestPermissions {
  const { hasAdminRights, hasGtmRights, userRoles } = useRBACPermissions()

  return {
    canResolve: hasAdminRights() || hasGtmRights(),
    isResolved: userRoles.isSuccess || userRoles.isError,
  }
}
